<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getCategoryRoute } from "@shopware-pwa/helpers-next";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { category } = useCategory();

const { subCategories, isCategoryFilterVisible, toggleCategoryFilter } = useSubCategoryNavigation();

const sortCategories = (categories: NavigationElement[]): NavigationElement[] => {
  if (!categories || categories.length === 0) return [];
  
  // Create a shallow copy to avoid mutating the original
  const result = [...categories];
  
  // Simple and safe sort that won't cause infinite loops
  result.sort((a, b) => {
    // Items without afterCategoryId come first
    if (!a.afterCategoryId) return -1;
    if (!b.afterCategoryId) return 1;
    
    // Simple string comparison as a fallback for stability
    return a.id.localeCompare(b.id);
  });
  
  // Process children non-recursively
  for (let i = 0; i < result.length; i++) {
    if (result[i].children && result[i].children.length > 0) {
      // Create a new array instead of modifying in place
      const sortedChildren = [];
      const childrenToProcess = [...result[i].children];
      
      // Sort children using the same simple logic
      childrenToProcess.sort((a, b) => {
        if (!a.afterCategoryId) return -1;
        if (!b.afterCategoryId) return 1;
        return a.id.localeCompare(b.id);
      });
      
      // Assign sorted children
      result[i] = {
        ...result[i],
        children: childrenToProcess
      };
    }
  }
  
  return result;
};

</script>
<template>
  <div
    class="accordion-item border-b border-b-custom-grey overflow-hidden"
    id="accordion-categories"
    v-if="subCategories.length"
  >
    <button
      class="accordion-header w-full text-left py-4 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggleCategoryFilter"
    >
      <span class="text-base font-bold text-dark-bg">{{ $t("listing.categories") }}</span>
      <svg
        id="icon-price"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[16px] w-[16px] transform transition-transform duration-200"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="[!isCategoryFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    <transition name="fade" mode="out-in">
      <div class="accordion-body" :class="[!isCategoryFilterVisible ? 'hidden' : '']">
        <ul class="space-y-2 mb-4">
          <li v-for="category in subCategories" class="block">
            <NuxtLink
              :to="formatLink(getCategoryRoute(category))"
              class="text-sm text-dark-bg font-normal hover:text-red-bg transition duration-300"
            >
              {{ category.name }}
            </NuxtLink>
            <ul v-if="category.childCount > 0">
              <li v-for="subCategory in category.children" class="block ml-4">
                <NuxtLink
                  :to="formatLink(getCategoryRoute(subCategory))"
                  class="text-sm text-dark-bg font-normal hover:text-red-bg transition duration-300"
                >
                  {{ subCategory.name }}
                </NuxtLink>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
